import { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext(null);

/**
 * Calculate addon price considering free addons limit
 * @param {Array} selectedAddons - Array of selected addons with quantity
 * @param {number} freeAddonsLimit - Number of free addons allowed
 * @returns {number} - Total price for addons (after applying free limit)
 */
function calculateAddonsPrice(selectedAddons, freeAddonsLimit = 0) {
  if (!selectedAddons || selectedAddons.length === 0) return 0;
  
  // Sort addons by price (cheapest first to maximize customer benefit)
  const sortedAddons = [...selectedAddons].sort((a, b) => a.price - b.price);
  
  let totalAddonsCount = 0;
  let addonsPrice = 0;
  
  for (const addon of sortedAddons) {
    for (let i = 0; i < addon.quantity; i++) {
      totalAddonsCount++;
      if (totalAddonsCount > freeAddonsLimit) {
        // This addon is not free, charge it
        addonsPrice += addon.price;
      }
    }
  }
  
  return addonsPrice;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((product, quantity = 1, selectedAddons = [], notes = '', selectedVariation = null) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.product.id === product.id && 
          JSON.stringify(item.selectedAddons) === JSON.stringify(selectedAddons) &&
          item.notes === notes &&
          item.selectedVariation?.id === selectedVariation?.id
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [...prev, { product, quantity, selectedAddons, notes, selectedVariation }];
    });
  }, []);

  const updateQuantity = useCallback((index, quantity) => {
    setItems(prev => {
      if (quantity <= 0) {
        return prev.filter((_, i) => i !== index);
      }
      const updated = [...prev];
      updated[index].quantity = quantity;
      return updated;
    });
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getItemPrice = useCallback((item) => {
    // Use variation price if selected, otherwise use product base price
    const basePrice = item.selectedVariation 
      ? item.selectedVariation.price 
      : item.product.price;
    const freeLimit = item.product.free_addons_limit || 0;
    const addonsPrice = calculateAddonsPrice(item.selectedAddons, freeLimit);
    return (basePrice + addonsPrice) * item.quantity;
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => {
      return total + getItemPrice(item);
    }, 0);
  }, [items, getItemPrice]);

  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      setIsOpen,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      getTotal,
      getItemCount,
      getItemPrice,
      calculateAddonsPrice
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
